import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  ElectricityPrice,
  ElectricityPriceRequest,
} from '../../../interface/electricity-price.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllApprovedElectricityPrice } from '../../../redux/slices/ApprovedElectricityPriceSlice';
import { setSelectedStation } from '../../../redux/slices/StationManagementSidePanelSlice';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';

/**
 * Component to list and change status of the electricity price for station
 * @param param0
 * @returns
 */
export default function ApprovedElectricityPrice({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t } = useTranslation('station');
  const dispatch = useAppDispatch();
  const {
    electricityPricing,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    // filterListLoading,
    // filters,
    // isFilterUpdated,
  } = useAppSelector((s) => s.approvedElectricityPrice);
  const navigate = useNavigate();

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStation: ElectricityPrice, newTab: boolean) => {
    if (chargingStation) {
      dispatch(
        setSelectedStation({
          id: chargingStation?._id,
          name: chargingStation?.chargingStationName,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/charging-station/approved/station/${chargingStation._id}/station-dashboard?searchBy=${chargingStation.chargingStationName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/charging-station/approved/station/${chargingStation._id}/station-dashboard`,
          search: `?searchBy=${chargingStation.chargingStationName}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: ElectricityPriceRequest) => {
    if (!loading) {
      dispatch(fetchAllApprovedElectricityPrice(data));
    }
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof ElectricityPrice,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof ElectricityPrice,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" usageCostView="approved" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('previousPrice'),
      accessorKey: 'existingElectricityCostByUtilityProvider' as keyof ElectricityPrice,
      sortAllow: false,
      /**
       * Function to display currentPrice
       * @param rowData
       * @returns
       */
      customData: (rowData: ElectricityPrice) => (
        <div className="electricity-cost">
          {rowData?.existingElectricityCostByUtilityProvider}/-
        </div>
      ),
    },
    {
      label: t('updatedPrice'),
      accessorKey: 'electricityCostByUtilityProvider' as keyof ElectricityPrice,
      sortAllow: false,
      /**
       * Function to display electricityCostByUtilityProvider
       * @param rowData
       * @returns
       */
      customData: (rowData: ElectricityPrice) => (
        <div className="electricity-cost">{rowData?.electricityCostByUtilityProvider}/-</div>
      ),
    },
  ];

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: ElectricityPriceRequest) => {
    // const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({ ...value });
  };

  /**
   * handleAvailabilityChange
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({ page, limit });
  };

  useEffect(() => {
    if (electricityPricing.length === 0) {
      fetchData({
        page,
        limit,
      });
    }
  }, []);

  return (
    <div className="table-content">
      <CustomTable
        dataKey="ElectricityPricePending"
        header={column}
        data={electricityPricing}
        serialNo
        limit={limit}
        loading={loading}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        version="v2"
        filters={[]}
        filterListToDispatch={{} as any}
        ReferenceCurrentPages={handleRefresh}
        rightTopContent={
          <div className="revenue-management-toggle-wrap">
            <CustomRadioButton
              name=""
              value="station"
              label={t('station')}
              selected={currentPage === 'station'}
              onChangeFunc={handleAvailabilityChange}
            />
            <CustomRadioButton
              name=""
              value="electricityPrice"
              label={t('electricityPrice')}
              selected={currentPage === 'electricityPrice'}
              onChangeFunc={handleAvailabilityChange}
            />
          </div>
        }
        refreshRequired
        height={window.innerHeight >= 700 ? 327 : 329}
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}
